import { useEffect, useRef, useState } from "react";
import { ADSTERRA, bannerDoc, nativeDoc } from "@/config/ads";
import { useTvMode } from "@/context/TvModeContext";

// Mount the ad iframe only when the slot nears the viewport (keeps first paint fast).
const useNearViewport = () => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return [ref, near];
};

const useIsDesktop = () => {
  const q = "(min-width: 768px)";
  const [desk, setDesk] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setDesk(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return desk;
};

const AdLabel = () => (
  <span data-testid="ad-supported-label" className="mb-1.5 block select-none font-mono text-[9px] uppercase tracking-[0.25em] text-white/30">
    Advertisement
  </span>
);

// 728x90 on desktop, 320x50 on phones; the box is reserved up front so nothing shifts.
const Banner = ({ className }) => {
  const desk = useIsDesktop();
  const unit = desk ? ADSTERRA.desktop : ADSTERRA.mobile;
  const [ref, near] = useNearViewport();
  return (
    <div className={`flex w-full flex-col items-center ${className}`} data-testid="ad-slot-leaderboard" data-ad-unit={desk ? "728x90" : "320x50"}>
      <AdLabel />
      <div ref={ref} style={{ width: unit.width, height: unit.height }} className="max-w-full overflow-hidden bg-[#0A0A0A]">
        {near && (
          <iframe
            key={unit.key}
            title="Advertisement"
            srcDoc={bannerDoc(unit)}
            width={unit.width}
            height={unit.height}
            scrolling="no"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            className="block border-0"
          />
        )}
      </div>
    </div>
  );
};

// Native unit styled as a grid card ("Sponsored").
const Native = ({ className }) => {
  const [ref, near] = useNearViewport();
  return (
    <div className={`block ${className}`} data-testid="ad-slot-native_card">
      <div ref={ref} className="relative aspect-video w-full overflow-hidden border border-white/10 bg-[#0A0A0A]">
        <span className="absolute left-2 top-2 z-10 border border-white/10 bg-black/60 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
          Sponsored
        </span>
        {near && (
          <iframe
            title="Sponsored"
            srcDoc={nativeDoc(ADSTERRA.native)}
            scrolling="no"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            className="absolute inset-0 h-full w-full border-0"
          />
        )}
      </div>
    </div>
  );
};

// Ads never render in the Kids hub (`disabled`) or TV Mode.
export const AdSlot = ({ type = "leaderboard", className = "", disabled = false }) => {
  const { tvMode } = useTvMode();
  if (disabled || tvMode) return null;
  if (type === "native_card") return <Native className={className} />;
  if (type === "leaderboard") return <Banner className={className} />;
  return null;
};
